import React, { useEffect, useMemo, useState } from 'react';

import { Modal, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { cycloneApiClient } from '@/shared/api/core/axios-instance';
import { getDeploymentsByRunIds } from '@/shared/api/deploy';
import { formatDateTime } from '@/shared/lib/format/dateTime';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { MetricsSummary } from '@/shared/ui/MetricsSummary';
import { type RunStatus, RunStatusTag } from '@/shared/ui/RunStatusTag';

import type { ExperimentListItem } from '@/entities/experiment/domain/experimentManagementTypes';
import type { RunListResponse } from '@/entities/run/domain/runManagementTypes';

import styles from './ExperimentComparisonModal.module.css';

type ExperimentComparisonModalProps = {
  open: boolean;
  experiments: ExperimentListItem[];
  onClose: () => void;
};

type ComparisonRunRow = {
  key: string;
  id: number;
  experimentName: string;
  runName: string;
  description: string;
  creator: string;
  startTime: string;
  metrics: Record<string, number> | null;
  status: RunStatus;
};

export const ExperimentComparisonModal: React.FC<ExperimentComparisonModalProps> = ({ open, experiments, onClose }) => {
  const [allRuns, setAllRuns] = useState<ComparisonRunRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [deploymentStatusMap, setDeploymentStatusMap] = useState<
    Map<number, { status: RunStatus; deploymentId?: string }>
  >(new Map());

  useEffect(() => {
    if (!open) {
      setAllRuns([]);
      setDeploymentStatusMap(new Map());
      return;
    }

    const fetchRuns = async () => {
      setLoading(true);
      try {
        const promises = experiments.map((exp) =>
          cycloneApiClient
            .get<RunListResponse>('/runs', {
              params: {
                experimentId: exp.id,
                size: 100,
              },
            })
            .then((response) => ({
              experimentName: exp.name,
              runs: response.data.runs || [],
            }))
            .catch((error) => {
              console.error(`Failed to fetch runs for experiment ${exp.name}:`, error);
              return {
                experimentName: exp.name,
                runs: [],
              };
            })
        );

        const results = await Promise.all(promises);

        const rows: ComparisonRunRow[] = [];
        const allRunIds: number[] = [];

        results.forEach((result) => {
          result.runs.forEach((run) => {
            allRunIds.push(run.id);
            rows.push({
              key: `${result.experimentName}-${run.id}`,
              id: run.id,
              experimentName: result.experimentName,
              runName: run.name,
              description: run.description || '-',
              creator: run.creator || '-',
              startTime: formatDateTime(run.createdAt),
              metrics: run.metrics || null,
              status: run.status as RunStatus,
            });
          });
        });

        setAllRuns(rows);

        if (allRunIds.length > 0) {
          try {
            const deploymentResponse = await getDeploymentsByRunIds({
              run_ids: allRunIds.map(String),
            });
            const statusMap = new Map<number, { status: RunStatus; deploymentId?: string }>();
            deploymentResponse.results.forEach((item) => {
              if (item.found && item.deployment) {
                const runId = parseInt(item.run_id, 10);
                statusMap.set(runId, {
                  status: item.deployment.status,
                  deploymentId: item.deployment.deployment_id,
                });
              }
            });
            setDeploymentStatusMap(statusMap);
          } catch (error) {
            console.error('Failed to fetch deployment status:', error);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    void fetchRuns();
  }, [open, experiments]);

  const columns: ColumnsType<ComparisonRunRow> = useMemo(
    () => [
      {
        title: '실험명',
        dataIndex: 'experimentName',
        key: 'experimentName',
        fixed: 'left',
        ellipsis: true,
        render: (text: string) => (
          <span className={styles.experimentName}>
            <EllipsisText text={text} />
          </span>
        ),
      },
      {
        title: '런명',
        dataIndex: 'runName',
        key: 'runName',
        ellipsis: true,
        render: (text: string) => (
          <span className={styles.runName}>
            <EllipsisText text={text} />
          </span>
        ),
      },
      {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        render: (text: string) => <AssetDescription description={text === '-' ? undefined : text} mode="tooltip" />,
      },
      {
        title: '소유자',
        dataIndex: 'creator',
        key: 'creator',
        width: 200,
        ellipsis: true,
        render: (text: string) => <EllipsisText text={text} />,
      },
      {
        title: '생성일',
        dataIndex: 'startTime',
        key: 'startTime',
        width: 180,
      },
      {
        title: '메트릭',
        dataIndex: 'metrics',
        key: 'metrics',
        width: 200,
        render: (metrics: Record<string, number> | null) => <MetricsSummary metrics={metrics} />,
      },
      {
        title: '상태',
        dataIndex: 'status',
        key: 'status',
        width: 200,
        align: 'left' as const,
        render: (status: RunStatus, record: ComparisonRunRow) => {
          const deploymentStatus = deploymentStatusMap.get(record.id);
          if (deploymentStatus) {
            return <RunStatusTag status={deploymentStatus.status} />;
          }
          return <RunStatusTag status={status} />;
        },
      },
    ],
    [deploymentStatusMap]
  );

  return (
    <Modal
      title={`실험 비교 (${experiments.length}개 실험)`}
      open={open}
      onCancel={onClose}
      width={1600}
      footer={null}
      className={styles.modal}>
      <Spin spinning={loading}>
        <Table
          dataSource={allRuns}
          columns={columns}
          pagination={false}
          scroll={{ y: 600 }}
          bordered
          size="small"
          className={styles.table}
        />
      </Spin>
    </Modal>
  );
};
